import { useQuery } from '@tanstack/react-query';
import { getApiBaseUrl } from '@/lib/api-base-url';
import { authFetch } from '@/lib/auth-fetch';

// Prisma.Decimal serializes as a string over JSON; we keep the raw string at
// the API boundary and parse to number where needed for display / arithmetic.
type DecimalString = string | null;

// Mirrors the backend `ExposureHedgeStatus`. Kept as raw strings so the chip
// filter on the UI side can switch on these values directly.
export type ExposureHedgeStatus =
  | 'Unhedged'
  | 'Partially Hedged'
  | 'Fully Hedged'
  | 'No Hedge Required';

export interface ExposureRow {
  orderId: string | null;
  commodityType: string | null;
  physicalSide: string | null;
  contractQtyMt: DecimalString;
  hedgeableQtyMt: DecimalString;
  hedgedLongMt: DecimalString;
  hedgedShortMt: DecimalString;
  hedgedNetMt: DecimalString;
  requestCount: number;
  pendingCount: number;
  approvedCount: number;
  executedCount: number;
  buyClientName: string | null;
  sellClientName: string | null;
  buyHedgeableMt: DecimalString;
  buyPricedMt: DecimalString;
  buyUnpricedMt: DecimalString;
  sellHedgeableMt: DecimalString;
  sellPricedMt: DecimalString;
  sellUnpricedMt: DecimalString;
  counterpartyName: string | null;
  hasExecutions: boolean;
  isBackToBack: boolean;
  hedgeStatus: ExposureHedgeStatus;
  hedgeCoveragePct: number;
  openExposureMt: DecimalString;
  pendingReferenceCount?: number;
  orderType: string | null;
}

export class HedgingExposureApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'HedgingExposureApiError';
  }
}

async function fetchExposure(): Promise<ExposureRow[]> {
  const response = await authFetch(`${getApiBaseUrl()}/hedging/exposure`);
  if (!response.ok) {
    throw new HedgingExposureApiError(
      `Request failed (${String(response.status)})`,
      response.status,
    );
  }
  return (await response.json()) as ExposureRow[];
}

export const HEDGING_EXPOSURE_QUERY_KEY = ['hedging', 'exposure'] as const;

export function useHedgingExposure() {
  return useQuery({
    queryKey: HEDGING_EXPOSURE_QUERY_KEY,
    queryFn: fetchExposure,
  });
}
